import { memo, useState, useEffect, useMemo, useCallback } from 'react'
import type { ReactNode } from 'react'
import { getIntl, localeInfo, getLocale } from 'umi'
import cn from 'classnames'

import Modal from '@salesforce/design-system-react/components/modal'
import Button from '@salesforce/design-system-react/components/button'

export type IMessageBox = {
  content: ReactNode
  type: 'success' | 'warning' | 'error' | 'wrench' | 'offline' | 'info' | 'default'
  title: ReactNode
  okText?: ReactNode
  cancelText?: string
  noCancel?: boolean
  noOk?: boolean
  disableClose?: boolean
  onConfirm?: (preventFinish: () => void) => void
  onClose?: () => void
  hideFooter?: boolean
  size?: 'small' | 'medium' | 'large'
  contentClassName?: string
  shouldInterrupt?: (
    payload: Record<string, any>,
    promise: IMessageBoxProps['promise'],
  ) => boolean | void
  [k: string]: any
}

type IMessageBoxProps = IMessageBox & {
  payload: Record<string, any>
  onUnMount: () => void
  promise: {
    resolve: (payload: IMessageBoxProps['payload']) => void
    reject: (payload: IMessageBoxProps['payload']) => void
  }
}

type IMessageBoxFn = React.NamedExoticComponent<IMessageBoxProps>

const MessageBox: IMessageBoxFn = memo(
  ({
    content,
    type,
    title,
    noCancel,
    noOk,
    onConfirm,
    onClose,
    okText,
    cancelText,
    onUnMount,
    shouldInterrupt,
    promise,
    payload,
    disableClose,
    size,
    hideFooter,
    contentClassName,
  }) => {
    const [visible, toggleVisible] = useState(false)
    const intl = getIntl(getLocale())
    const mTitle = useMemo(() => {
      return title || intl.messages['Global.Components.Prompt.title']
    }, [title, intl])
    const mOkText = useMemo(() => {
      return okText || intl.messages['Global.Components.Prompt.button.confirm']
    }, [okText, intl])
    const mCancelText = useMemo(() => {
      return cancelText || intl.messages['Global.Components.Stepper.button.cancel']
    }, [cancelText, intl])

    const antdLocale = useMemo(() => {
      let lang = getLocale() || 'zh-CN'
      if (lang.includes('en')) lang = 'en-US'
      else if (lang.includes('zh')) lang = 'zh-CN'
      return localeInfo[lang]?.antd
    }, [localeInfo, getLocale])

    const handleConfirm = useCallback(async () => {
      const isInterrupt = await shouldInterrupt?.(payload, promise)
      if (isInterrupt) return
      let hasTriggerVisibleChange: boolean = false
      const prevent = () => {
        hasTriggerVisibleChange = true
      }
      const r = (onConfirm?.(prevent) as unknown) as Promise<any>
      if (r?.then) {
        r.then(() => {
          if (!hasTriggerVisibleChange) {
            toggleVisible(false)
            onUnMount()
          }
          promise.resolve(payload)
        })
      } else {
        if (!hasTriggerVisibleChange) {
          toggleVisible(false)
          onUnMount()
        }
        promise.resolve(payload)
      }
    }, [onConfirm, promise.resolve, onUnMount, toggleVisible, shouldInterrupt])

    const handleCancel = (forceClose?: boolean) => () => {
      if (!forceClose) {
        promise.reject(payload)
        onUnMount()
      }
      onClose?.()
      toggleVisible(false)
    }

    useEffect(() => {
      toggleVisible(true)
      return () => {
        onUnMount()
      }
    }, [])

    return (
      <Modal
        disableClose={disableClose}
        isOpen={visible}
        contentClassName={cn('sf-global-modal-padding', contentClassName)}
        dismissOnClickOutside={false}
        onRequestClose={handleCancel(true)}
        prompt={type === 'default' ? undefined : type}
        heading={mTitle}
        size={size}
        footer={
          Boolean(hideFooter) ? undefined : (
            <>
              {!noCancel && <Button label={mCancelText} onClick={handleCancel()} />}
              {!noOk && <Button variant="brand" label={mOkText} onClick={handleConfirm} />}
            </>
          )
        }
      >
        {content}
      </Modal>
    )
  },
)

export default MessageBox
